import type { Filter, Sort } from '@crm/contracts';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import type { PipelineView } from '../../lib/client.ts';
import { andFilter, flatConditions } from '../../lib/list-view.ts';
import { useSession } from '../../lib/session.tsx';
import { useSettings } from '../../lib/settings.ts';
import { cn } from '../../lib/utils.ts';
import { Badge } from '../ui/badge.tsx';
import { KanbanBoard, type BoardSource } from './kanban-board.tsx';
import type { ListApi } from './record-list.tsx';

/**
 * Multi board: every pipeline's board stacked, one lane each, like Jira's swimlanes but in the
 * Leve look (a card per lane, the boards inside unchanged). Each lane is the same `KanbanBoard`
 * the Board layout draws, over its own pipeline's search and counts (`apiFor`), so a drag in a
 * lane moves the record in that pipeline only.
 *
 * A lane folds from its header; folded lanes are remembered per user (`collapsedBoardLanes`)
 * and mount no board, so they ask for nothing but their total. The list's filter applies to
 * every lane, except a stage condition, which only means something in its own pipeline: a lane
 * keeps it only when the stages it names are that lane's.
 */
export function MultiBoard<T>({
  name,
  noun,
  pipelines,
  apiFor,
  q,
  filter,
  sort,
  hideTerminal,
  canMove,
  renderCard,
  onOpen,
  onMoved,
}: {
  name: string;
  /** What a lane's total counts: `{ one: 'project', many: 'projects' }`. */
  noun: { one: string; many: string };
  pipelines: readonly PipelineView[];
  apiFor: (pipeline: PipelineView) => ListApi<T>;
  q: string | undefined;
  filter: Filter | undefined;
  sort: Sort | undefined;
  hideTerminal: boolean;
  canMove: boolean;
  renderCard: (item: T) => ReactNode;
  onOpen: (item: T) => void;
  onMoved: () => Promise<void>;
}) {
  const session = useSession();
  const [settings, update] = useSettings(session.me.user.id);
  const folded = new Set(settings.collapsedBoardLanes);
  const toggle = (id: string) =>
    update({
      collapsedBoardLanes: folded.has(id)
        ? settings.collapsedBoardLanes.filter((x) => x !== id)
        : [...settings.collapsedBoardLanes, id],
    });
  return (
    <div className="flex flex-col gap-4" data-testid={`${name}-multi-board`}>
      {pipelines.map((p) => (
        <Lane
          key={p.id}
          name={name}
          noun={noun}
          pipeline={p}
          api={apiFor(p)}
          q={q}
          filter={filter}
          sort={sort}
          collapsed={folded.has(p.id)}
          onToggle={() => toggle(p.id)}
          hideTerminal={hideTerminal}
          canMove={canMove}
          renderCard={renderCard}
          onOpen={onOpen}
          onMoved={onMoved}
        />
      ))}
    </div>
  );
}

function Lane<T>({
  name,
  noun,
  pipeline,
  api,
  q,
  filter,
  sort,
  collapsed,
  onToggle,
  hideTerminal,
  canMove,
  renderCard,
  onOpen,
  onMoved,
}: {
  name: string;
  noun: { one: string; many: string };
  pipeline: PipelineView;
  api: ListApi<T>;
  q: string | undefined;
  filter: Filter | undefined;
  sort: Sort | undefined;
  collapsed: boolean;
  onToggle: () => void;
  hideTerminal: boolean;
  canMove: boolean;
  renderCard: (item: T) => ReactNode;
  onOpen: (item: T) => void;
  onMoved: () => Promise<void>;
}) {
  // The list's filter, less any stage condition naming another pipeline's stages.
  const laneFilter = useMemo(() => {
    const own = new Set(pipeline.stages.map((s) => s.id));
    return andFilter(
      flatConditions(filter).filter(
        (c) => c.field !== 'stageId' || [c.value].flat().every((v) => own.has(String(v))),
      ),
    );
  }, [filter, pipeline]);
  const scope = useMemo(
    () => ({ ...(q ? { q } : {}), ...(laneFilter ? { filter: laneFilter } : {}) }),
    [q, laneFilter],
  );
  const source = useMemo<BoardSource<T>>(
    () => ({
      scope: [scope, sort ?? null, pipeline.id],
      counts: () => api.counts(scope),
      page: (stageId, cursor, limit) =>
        api.search({
          ...scope,
          filter: laneFilter
            ? { and: [laneFilter, { field: 'stageId', op: 'eq', value: stageId }] }
            : { field: 'stageId', op: 'eq', value: stageId },
          ...(sort ? { sort: [sort] } : {}),
          limit,
          ...(cursor ? { cursor } : {}),
        }),
      move: api.move,
      idOf: api.idOf,
      stageOf: api.stageOf,
      titleOf: api.titleOf,
      moved: api.moved,
    }),
    [scope, laneFilter, sort, api, pipeline.id],
  );
  // The lane's total, also while folded: the board's own counts query (same key, so one request).
  const counts = useQuery({
    queryKey: ['board', name, ...source.scope, 'counts'],
    queryFn: source.counts,
  });
  const total = counts.data?.reduce((n, c) => n + c.count, 0);
  const bodyId = `lane-${pipeline.id}`;
  return (
    <section
      className="bg-card border-border rounded-card shadow-card min-w-0 border"
      aria-label={pipeline.name}
      data-testid="board-lane"
      data-pipeline-id={pipeline.id}
      data-collapsed={collapsed || undefined}
    >
      <h3>
        <button
          type="button"
          className="hover:bg-muted/60 flex w-full items-center gap-2 rounded-card px-4 py-3 text-left"
          aria-expanded={!collapsed}
          aria-controls={bodyId}
          onClick={onToggle}
        >
          <ChevronRight
            className={cn(
              'text-muted-foreground size-4 shrink-0 transition-transform',
              !collapsed && 'rotate-90',
            )}
            aria-hidden
          />
          {/* The spaces keep the button's spoken name apart ("Sales, Default, 5 projects"); a
              flex row lays them out as nothing. */}
          <span className="truncate font-semibold">{pipeline.name}</span>{' '}
          {pipeline.isDefault && <Badge variant="secondary">Default</Badge>}{' '}
          <span className="text-muted-foreground text-xs tabular-nums" data-testid="lane-total">
            {total === undefined ? '…' : `${total} ${total === 1 ? noun.one : noun.many}`}
          </span>
        </button>
      </h3>
      {!collapsed && (
        <div id={bodyId} className="border-border border-t px-3 pt-3">
          <KanbanBoard
            name={name}
            pipeline={pipeline}
            source={source}
            hideTerminal={hideTerminal}
            canMove={canMove}
            onOpen={onOpen}
            onMoved={onMoved}
            renderCard={renderCard}
          />
        </div>
      )}
    </section>
  );
}
